import type { QueryClient } from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { projectsService } from '@/features/project/api/projects';
import { getActiveProjectSnapshot } from '@/shared/lib/active-project-store';
import type { User } from '@/types/user';

const FALLBACK_DESTINATION = '/';

interface ResolveOptions {
  queryClient: QueryClient;
  defaultProjectId?: string | null;
  redirectUrl?: string | null;
}

/**
 * Vet a captured `?redirect=` / `oauth_redirect` deep link against the
 * freshly-authenticated identity.
 *
 * The redirect is the URL the user was on BEFORE authenticating. On an account
 * switch (user A's session lapses on `/project/{A}/…`, user B then logs in on
 * that same bounced login URL) the redirect points at the PREVIOUS user's
 * building. Honoring it blindly dumps the new identity into a foreign
 * `/project/{id}` and 403s the entire page — the building GET, its concepts,
 * estimates and albums all return AUTHORIZATION_DENIED ("This action is
 * unauthorized"). That is the cross-user 403 storm users hit after switching
 * accounts in the same browser.
 *
 * Rules:
 *   - non-project paths (`/account`, `/history`, …) are identity-agnostic → safe
 *   - `/project/{id}/…` is honored ONLY when `{id}` belongs to THIS user
 *
 * Returns the redirect path when safe, or `null` to fall back to the home
 * resolver (which lands the user on their own last-opened / first building).
 *
 * Ownership is checked by list membership — same approach (and same pagination
 * caveat) as the `/` root resolver; a still-owned building beyond the first
 * page degrades safely to the home resolver rather than to a foreign 403.
 */
export async function resolveSafeRedirect(
  queryClient: QueryClient,
  redirectPath: string | null | undefined
): Promise<string | null> {
  if (!redirectPath) return null;

  const projectId = redirectPath.match(/^\/project\/([^/?#]+)/)?.[1];
  if (!projectId) return redirectPath;

  try {
    const response = await queryClient.fetchQuery({
      queryKey: cacheKeys.projects.list(),
      queryFn: () => projectsService.list({ sort: '-created_at' }),
    });
    return response.data.some((project) => project.id === projectId)
      ? redirectPath
      : null;
  } catch {
    // Network blip — don't risk dropping the new identity into a foreign 403;
    // fall back to the home resolver instead of honoring an unvetted redirect.
    return null;
  }
}

/**
 * Where the user lands right after a cold sign-in / sign-up / verify.
 *
 * Priority:
 *   1. caller-provided `redirectUrl` (?redirect= search param) — user came from a deep link
 *   2. server-issued `defaultProjectId` (auto-created starter project for brand-new accounts)
 *   3. the building the root resolver would pick: last-opened → first
 *   4. `/` (no buildings yet → first-run create state; or the list failed —
 *      don't block a sign-in on a network blip, let the root retry)
 *
 * Step 3 exists so a sign-in navigates ONCE. Returning `/` for an ordinary
 * returning user meant landing on the root resolver, which fetched the very
 * same list and redirected again — an extra spinner on every login.
 *
 * Cold-login only: callers wire this into login / register / verify-email / reset-password.
 * Warm session-restore stays on whatever screen the user was on.
 */
export async function resolvePostLoginDestination(
  user: User | null,
  options: ResolveOptions
): Promise<string> {
  // A `?redirect=` deep link is honored only when it is safe for THIS identity
  // (see resolveSafeRedirect) — a foreign/stale `/project/{id}` redirect falls
  // through to the normal resolution below instead of 403-ing the page.
  if (options.redirectUrl) {
    const safe = await resolveSafeRedirect(
      options.queryClient,
      options.redirectUrl
    );
    if (safe) return safe;
  }
  if (options.defaultProjectId) return `/project/${options.defaultProjectId}`;

  try {
    const response = await options.queryClient.fetchQuery({
      queryKey: cacheKeys.projects.list(),
      queryFn: () => projectsService.list({ sort: '-created_at' }),
    });
    const projects = response.data;
    if (projects.length === 0) return FALLBACK_DESTINATION;

    // Same rule as the root resolver (`app/page.tsx`), read from the same
    // store: the last building this identity had open, else the newest one.
    // A cold sign-in after logout finds nothing here (logout wipes the
    // user-scoped keys) and lands on the newest building.
    const lastProjectId = user ? getActiveProjectSnapshot(user.id) : null;
    const target =
      lastProjectId && projects.some((project) => project.id === lastProjectId)
        ? lastProjectId
        : projects[0]?.id;

    return target ? `/project/${target}` : FALLBACK_DESTINATION;
  } catch {
    // Network blip — hand over to the root resolver instead of failing the
    // sign-in; it retries the list and shows a real error state.
    return FALLBACK_DESTINATION;
  }
}
